// src/components/TradeSelector/TradeSelector.tsx
import { loading, search } from "@simplybusiness/icons";
import { Combobox, Icon } from "@simplybusiness/mobius";
import { forwardRef, useCallback } from "react";

// src/components/TradeSelector/TradeSelectorOption.tsx
import { Text } from "@simplybusiness/mobius";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
function TradeSelectorOption({
  option: { value, definition }
}) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Text, { variant: "h4", children: value }),
    definition && /* @__PURE__ */ jsx(Text, { children: definition })
  ] });
}

// src/components/TradeSelector/constants.ts
var LABELS = {
  default: "Select a trade",
  ai: "Describe your trade"
};
var PLACEHOLDERS = {
  default: "e.g. plumber",
  ai: "e.g. I bake cakes"
};
var TRADES_ENDPOINTS = {
  production: "https://trade.api.simplybusiness.com/v1/trades",
  staging: "https://rambdas-staging.simplybusiness.co.uk/rambdas/trade-selector-chopin"
};
var DEFAULT_TRADES_ENDPOINT = false ? TRADES_ENDPOINTS.production : TRADES_ENDPOINTS.staging;

// src/components/TradeSelector/groupResults.ts
var groupResults = (results) => {
  const mapped = results.map(
    ({ value, vertical, definition }) => ({
      label: value,
      value,
      vertical,
      definition
    })
  );
  const { exact, suggested } = results.reduce(
    (groups, result, index) => {
      if (result.type === "exact") {
        groups.exact.push(mapped[index]);
      } else if (result.type === "suggested") {
        groups.suggested.push(mapped[index]);
      }
      return groups;
    },
    { exact: [], suggested: [] }
  );
  if (!suggested.length || !exact.length) {
    return mapped;
  }
  return [
    {
      heading: "Exact",
      options: exact
    },
    {
      heading: "Suggested",
      options: suggested
    }
  ];
};

// src/hooks/useFetchTrades.ts
import { useMutation } from "@tanstack/react-query";
var useFetchTrades = (options) => {
  const {
    site,
    vertical,
    tradesEndpoint = DEFAULT_TRADES_ENDPOINT,
    searchScope,
    variant = "default",
    primaryDetail,
    transformResults,
    onError
  } = options;
  const mutation = useMutation({
    mutationFn: async (query) => {
      const attemptFetch = async (fetchVariant) => {
        const url = makeTradesUrl({
          site,
          vertical,
          searchScope,
          query,
          tradesEndpoint,
          variant: fetchVariant,
          primaryDetail
        });
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        try {
          const json = await response.json();
          return json;
        } catch (error) {
          console.error("Failed to parse Trade API response as JSON:", error);
          throw new Error("Unable to fetch trades at this time.");
        }
      };
      try {
        const data = await attemptFetch(variant);
        if (variant === "ai" && (!data.results || data.results.length === 0)) {
          const fallbackData = await attemptFetch("default");
          return transformResults ? transformResults(fallbackData.results) : fallbackData.results;
        }
        return transformResults ? transformResults(data.results) : data.results;
      } catch (error) {
        if (variant === "ai") {
          const fallbackData = await attemptFetch("default");
          return transformResults ? transformResults(fallbackData.results) : fallbackData.results;
        }
        throw error;
      }
    }
  });
  const originalMutate = mutation.mutate;
  const wrappedMutate = (query) => {
    originalMutate(query, {
      onError: (error) => {
        console.error("Failed to fetch trades:", error);
        onError?.(error);
      }
    });
  };
  return {
    ...mutation,
    mutate: wrappedMutate
  };
};

// src/components/TradeSelector/TradeSelector.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var TradeSelector = forwardRef(
  ({
    onSelectTrade,
    site,
    vertical,
    searchScope,
    tradesEndpoint = DEFAULT_TRADES_ENDPOINT,
    placeholder,
    variant = "default",
    primaryDetail,
    ...otherProps
  }, ref) => {
    const tradesMutation = useFetchTrades({
      site,
      vertical,
      searchScope,
      tradesEndpoint,
      variant,
      primaryDetail,
      transformResults: groupResults
    });
    const fetchTrades = useCallback(
      async (query) => {
        return await tradesMutation.mutateAsync(query);
      },
      [site, vertical, searchScope, tradesEndpoint, variant, primaryDetail]
    );
    const searchPlaceholder = placeholder || PLACEHOLDERS[variant];
    const prefixIcon = tradesMutation.isPending ? /* @__PURE__ */ jsx2(Icon, { icon: loading, spin: true }) : /* @__PURE__ */ jsx2(Icon, { icon: search });
    return /* @__PURE__ */ jsx2(
      Combobox,
      {
        ref,
        label: LABELS[variant],
        placeholder: searchPlaceholder,
        asyncOptions: fetchTrades,
        onSelected: onSelectTrade,
        optionComponent: TradeSelectorOption,
        "data-variant": variant,
        icon: prefixIcon,
        ...otherProps
      }
    );
  }
);
TradeSelector.displayName = "TradeSelector";

// src/components/TradeSelector/makeTradesUrl.ts
function makeTradesUrl({
  site,
  vertical,
  query,
  tradesEndpoint,
  variant,
  searchScope,
  primaryDetail = false
}) {
  const url = new URL(tradesEndpoint);
  url.searchParams.append("service_channel_identifier", site);
  url.searchParams.append("vertical", vertical);
  url.searchParams.append("show_trade_definitions", "true");
  if (searchScope) {
    url.searchParams.append("search_scope", searchScope);
  }
  if (primaryDetail) {
    url.searchParams.append("primary_detail", "true");
  }
  const usesShortParam = tradesEndpoint.toLowerCase().includes("rambdas") || tradesEndpoint.includes("/trade_search");
  url.searchParams.append(usesShortParam ? "q" : "query", query);
  if (variant === "ai") {
    url.searchParams.append("ai_results", "true");
    url.searchParams.append("ai_strategy", "rag-strategy");
  }
  return url.toString();
}
export {
  DEFAULT_TRADES_ENDPOINT,
  TradeSelector,
  TradeSelectorOption,
  makeTradesUrl,
  useFetchTrades
};
